import { yupResolver } from '@hookform/resolvers/yup';
// styles
import classnames from 'classnames/bind';
import Button from 'components/Button';
import Input from 'components/Input';
// components
import Typography from 'components/Typography';
// containers
import ManagementWrapper from 'containers/ManagementWrapper';
import T from 'prop-types';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ClASSESS_FULL, getManageClassRoute, getManageUserRoute } from 'routes';
// utils
import { notifyWithSnack } from 'store/actions/notifications';
import { getUser, resetAllPasswords, updateStudent } from 'store/actions/user';
import * as yup from 'yup';

import styles from './ResetPassword.module.scss';

const cx = classnames.bind(styles);

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .required('New password field is required.')
    .min(8, 'Password must be at least 8 characters.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*(\d|\W)).{8,}$/,
      'Passwords must include an upper and lowercase letter, and at least one number or special character.',
    ),
  repeatPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Passwords must match.'),
});

const ResetPassword = ({ resetAll = false }) => {
  const { classId, userId } = useParams();
  const { currClass = {}, student = {} } = useSelector(({ user }) => {
    const currClass = user.classrooms.find(({ id }) => +id === +classId);
    const student = currClass?.students.find(({ id }) => +id === +userId);

    return { currClass, student };
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let crumbs = { 'all classes': ClASSESS_FULL, [currClass.name]: getManageClassRoute(classId) };
  if (!resetAll) {
    const userCreds = student.first_name
      ? `${student.first_name} ${student.last_name_initial || ''}${student.last_name_initial ? '.' : ''}`
      : student.username;
    crumbs = Object.assign(crumbs, { [userCreds]: getManageUserRoute(classId, userId) });
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: 'onChange' });

  const handleFormSubmit = (data) => {
    if (resetAll) {
      dispatch(resetAllPasswords(classId, currClass.name, { password: data.newPassword })).then(() => {
        dispatch(getUser());
        navigate(getManageClassRoute(classId));
      });
      return;
    }

    dispatch(updateStudent(userId, classId, { password: data.newPassword }))
      .then(() => {
        dispatch(
          notifyWithSnack({
            message: `${student.first_name ? student.first_name : student.username}'s password has been reset.`,
          }),
        );
        navigate(getManageUserRoute(classId, userId));
      })
      .catch(() =>
        dispatch(
          notifyWithSnack({
            message: `Couldn't reset ${student.first_name ? student.first_name : student.username}'s password.`,
            type: 'error',
          }),
        ),
      );
  };

  return (
    <ManagementWrapper breadcrumbs={crumbs}>
      <Typography className="mgb-15" type="h4">
        {resetAll
          ? 'Reset All Passwords'
          : `Reset ${student.first_name ? student.first_name : student.username}'s password`}
      </Typography>

      <Typography className="mgb-35">
        Resetting the password for all students will reset the master password for the classroom.
      </Typography>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Input
          label="Enter new password"
          name="newPassword"
          type="password"
          isPassword={true}
          inputRef={register('newPassword', {
            required: true,
            minLength: 8,
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
          })}
          errors={errors}
          isPassword
        />
        <Input
          label="Re-enter new password"
          name="repeatPassword"
          type="password"
          inputRef={register('repeatPassword', {
            required: true,
            validate: (value) => value === watch('newPassword'),
          })}
          errors={errors}
          isPassword
        />
        <Button className={cx('submit-button', 'mgt-50')} label="SAVE" submit />
      </form>
    </ManagementWrapper>
  );
};

ResetPassword.propTypes = {
  resetAll: T.bool,
};

export default ResetPassword;
